import { useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Upload, Plus, Star, Trash2, Download, ChevronLeft, ChevronRight, X, ImageIcon } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { SpaceSection } from "@/components/SpaceSection";
import { downloadUrl } from "@/pages/FilesPage";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const Lightbox = ({ photos, index, onClose, onNav, onToggleFav, onDelete, myId }) => {
  const photo = photos[index];
  if (!photo) return null;
  const isFav = photo.favorited_by?.includes(myId);
  return (
    <div className="fixed inset-0 z-50 bg-black/95 flex flex-col" data-testid="photo-lightbox">
      <div className="flex items-center justify-between p-4 text-white">
        <p className="text-sm truncate">{photo.name}</p>
        <div className="flex items-center gap-3">
          <button onClick={() => onToggleFav(photo)} data-testid="lightbox-favorite-btn">
            <Star className="h-5 w-5" fill={isFav ? "#F9AB00" : "none"} stroke={isFav ? "#F9AB00" : "white"} />
          </button>
          <a href={downloadUrl(photo.id)} download={photo.name} data-testid="lightbox-download-btn">
            <Download className="h-5 w-5" />
          </a>
          <button onClick={() => onDelete(photo)} data-testid="lightbox-delete-btn">
            <Trash2 className="h-5 w-5 text-red-400" />
          </button>
          <button onClick={onClose} data-testid="lightbox-close-btn">
            <X className="h-6 w-6" />
          </button>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center relative px-14 pb-6">
        {index > 0 && (
          <button onClick={() => onNav(index - 1)} data-testid="lightbox-prev-btn"
            className="absolute left-3 text-white/70 hover:text-white p-2">
            <ChevronLeft className="h-8 w-8" />
          </button>
        )}
        <img src={downloadUrl(photo.id)} alt={photo.name} className="max-h-full max-w-full object-contain" />
        {index < photos.length - 1 && (
          <button onClick={() => onNav(index + 1)} data-testid="lightbox-next-btn"
            className="absolute right-3 text-white/70 hover:text-white p-2">
            <ChevronRight className="h-8 w-8" />
          </button>
        )}
      </div>
    </div>
  );
};

export default function PhotosPage() {
  const { id: workspaceId } = useParams();
  const qc = useQueryClient();
  const inputRef = useRef(null);
  const [albumFilter, setAlbumFilter] = useState(null);
  const [lightbox, setLightbox] = useState(-1);
  const [albumOpen, setAlbumOpen] = useState(false);
  const [albumName, setAlbumName] = useState("");
  const [uploading, setUploading] = useState(false);

  const { data: albums = [] } = useQuery({
    queryKey: ["albums", workspaceId],
    queryFn: async () => (await api.get(`/workspaces/${workspaceId}/albums`)).data,
  });
  const { data: photos = [] } = useQuery({
    queryKey: ["photos", workspaceId, albumFilter],
    queryFn: async () => {
      const params = { images_only: true };
      if (albumFilter) params.album_id = albumFilter;
      return (await api.get(`/workspaces/${workspaceId}/files`, { params })).data;
    },
  });
  const { data: me } = useQuery({ queryKey: ["me"], queryFn: async () => (await api.get("/auth/me")).data });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["photos", workspaceId] });
    qc.invalidateQueries({ queryKey: ["albums", workspaceId] });
    qc.invalidateQueries({ queryKey: ["files", workspaceId] });
  };

  const upload = async (fileList) => {
    setUploading(true);
    for (const f of fileList) {
      const form = new FormData();
      form.append("file", f);
      if (albumFilter) form.append("album_id", albumFilter);
      try {
        await api.post(`/workspaces/${workspaceId}/files`, form);
      } catch (e) {
        toast.error(`${f.name}: ${apiError(e)}`);
      }
    }
    setUploading(false);
    invalidate();
    toast.success(fileList.length > 1 ? `${fileList.length} fotos subidas` : "Foto subida");
  };

  const createAlbum = async () => {
    if (!albumName.trim()) return toast.error("El nombre es obligatorio");
    try {
      await api.post(`/workspaces/${workspaceId}/albums`, { name: albumName });
      invalidate();
      setAlbumOpen(false);
      setAlbumName("");
      toast.success("Álbum creado");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const toggleFav = async (photo) => {
    await api.post(`/files/${photo.id}/favorite`);
    invalidate();
  };

  const deletePhoto = async (photo) => {
    await api.delete(`/files/${photo.id}`);
    setLightbox(-1);
    invalidate();
    toast.success("Foto enviada a la papelera");
  };

  return (
    <SpaceSection title="Fotos" actions={
      <div className="flex gap-2">
        <Button size="sm" variant="outline" data-testid="photos-new-album-btn" onClick={() => setAlbumOpen(true)}>
          <Plus className="h-4 w-4 mr-1" /> Álbum
        </Button>
        <Button size="sm" data-testid="photos-upload-btn" onClick={() => inputRef.current?.click()} disabled={uploading}>
          <Upload className="h-4 w-4 mr-1" /> {uploading ? "Subiendo..." : "Subir fotos"}
        </Button>
      </div>
    }>
      <input ref={inputRef} type="file" multiple accept="image/*" hidden data-testid="photos-upload-input"
        onChange={(e) => { if (e.target.files.length) upload([...e.target.files]); e.target.value = ""; }} />

      <div className="flex gap-3 overflow-x-auto pb-2" data-testid="albums-strip">
        <button onClick={() => setAlbumFilter(null)} data-testid="album-all-btn"
          className={`shrink-0 w-28 rounded-xl border p-2 text-left transition-colors ${!albumFilter ? "border-primary bg-accent/50" : "hover:border-primary/40"}`}>
          <div className="h-16 rounded-lg bg-secondary flex items-center justify-center mb-1.5">
            <ImageIcon className="h-5 w-5 text-muted-foreground" />
          </div>
          <p className="text-xs font-semibold truncate">Todas</p>
          <p className="text-[10px] text-muted-foreground">las fotos</p>
        </button>
        {albums.map((a) => (
          <button key={a.id} onClick={() => setAlbumFilter(albumFilter === a.id ? null : a.id)}
            data-testid={`album-card-${a.id}`}
            className={`shrink-0 w-28 rounded-xl border p-2 text-left transition-colors ${albumFilter === a.id ? "border-primary bg-accent/50" : "hover:border-primary/40"}`}>
            {a.cover_file_id ? (
              <img src={downloadUrl(a.cover_file_id)} alt="" className="h-16 w-full rounded-lg object-cover mb-1.5" loading="lazy" />
            ) : (
              <div className="h-16 rounded-lg bg-secondary flex items-center justify-center mb-1.5">
                <ImageIcon className="h-5 w-5 text-muted-foreground" />
              </div>
            )}
            <p className="text-xs font-semibold truncate">{a.name}</p>
            <p className="text-[10px] text-muted-foreground">{a.photo_count} fotos</p>
          </button>
        ))}
      </div>

      {photos.length === 0 ? (
        <Card className="p-12 text-center border-dashed">
          <ImageIcon className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
          <p className="text-sm text-muted-foreground">
            {albumFilter ? "Este álbum está vacío. Sube fotos con el álbum seleccionado." : "Sube tus primeras fotos. Cada imagen conserva su contexto."}
          </p>
        </Card>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2" data-testid="photos-grid">
          {photos.map((p, i) => (
            <button key={p.id} onClick={() => setLightbox(i)} data-testid={`photo-thumb-${p.id}`}
              className="relative aspect-square rounded-lg overflow-hidden group">
              <img src={downloadUrl(p.id)} alt={p.name} loading="lazy"
                className="h-full w-full object-cover group-hover:scale-105 transition-transform" />
              {p.favorited_by?.includes(me?.id) && (
                <Star className="absolute top-1.5 right-1.5 h-4 w-4" fill="#F9AB00" stroke="#F9AB00" />
              )}
            </button>
          ))}
        </div>
      )}

      {lightbox >= 0 && (
        <Lightbox photos={photos} index={lightbox} onClose={() => setLightbox(-1)}
          onNav={setLightbox} onToggleFav={toggleFav} onDelete={deletePhoto} myId={me?.id} />
      )}

      <Dialog open={albumOpen} onOpenChange={setAlbumOpen}>
        <DialogContent className="sm:max-w-sm" data-testid="create-album-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Nuevo álbum</DialogTitle>
            <DialogDescription>Agrupa fotos de un viaje, evento o proyecto.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nombre</Label>
              <Input data-testid="album-name-input" value={albumName} onChange={(e) => setAlbumName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && createAlbum()} placeholder="Ej: Japón 2027" maxLength={100} />
            </div>
            <Button className="w-full" data-testid="album-create-submit-btn" onClick={createAlbum}>Crear álbum</Button>
          </div>
        </DialogContent>
      </Dialog>
    </SpaceSection>
  );
}
